import { getAccessToken, refreshToken } from "Helpers/api";
import { MappingChange, TenantMapping } from "Pages/Mapping/Mapping";

let checkURL = process.env.REACT_APP_API_URL_PCI_COMPLIANCE as string;
const urlhost = window.location.host;
if(urlhost.includes('ev.')){
    checkURL = 'https://pci-compliance.dev.venzagrc.com/api/pci-compliance';
}
if(urlhost.includes('tg.')){
    checkURL = 'https://pci-compliance.stg.venzagrc.com/api/pci-compliance';
}
if(!urlhost.includes('ev.') && !urlhost.includes('tg.') && !urlhost.includes(':8')){
    checkURL = 'https://pci-compliance.prod.venzagrc.com/api/pci-compliance';
}

const baseUrl = checkURL as string;

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${getAccessToken()}`,
});

const authorizedFetch = async (
  url: string,
  options: RequestInit,
  retryCount = 0 // start at 0
): Promise<Response> => {
  const response = await fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${getAccessToken()}`,
    },
  });

  if (response.status === 401 && retryCount < 3) {
    try {
      await refreshToken();
      return authorizedFetch(url, options, retryCount + 1);
    } catch (err) {
      console.error("Token refresh failed:", err);
      throw err;
    }
  }

  return response;
};


export const getProvidersForTenant = async (
  tenantId: string,
  tenantName: string
) => {
  const url = `${baseUrl}/tenants/providers?tenant_id=${tenantId}&tenant_name=${tenantName}`;
  try {
    const response = await authorizedFetch(url, { method: "GET", headers: getAuthHeaders() });
    if (!response.ok) {
      throw new Error(`API call failed: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching provider list:", error);
    throw error;
  }
};

export const updateMappingStatus = async (
  tenantId: number,
  providerId: number,
  status: 'enable' | 'disable'
) => {
  const url = `${baseUrl}/tenant-provider-mappings/update-status?tenant_id=${tenantId}&provider_id=${providerId}&status=${status}`;

  try {
    const response = await authorizedFetch(url, { method: 'POST', headers: getAuthHeaders() });
    if (!response.ok) {
      throw new Error(`API call failed with status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Error updating mapping status:", error);
    throw error;
  }
};

interface TenantInfo {
  id: string | number;
  name: string;
}

export interface PropertyMerchantMapping {
  property_id?: string | null;
  property_name: string | null;
  mid?: string | null;
}

interface ConfirmMappingRequest {
  provider_id: number | null;
  tenant: TenantInfo;
  property_merchant_mappings: PropertyMerchantMapping[];
}

export const confirmMapping = async (
  tenantInfo: TenantMapping,
  propertyMerchantMappings: PropertyMerchantMapping[]
) => {
  const url = `${baseUrl}/property-merchant-mappings`;

  const payload: ConfirmMappingRequest = {
    provider_id: tenantInfo.providerId,
    tenant: {
      id: tenantInfo.id,
      name: tenantInfo.name,
    },
    property_merchant_mappings: propertyMerchantMappings,
  };

  try {
    const response = await authorizedFetch(url, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => null);
      throw {
        status: response.status,
        data: errorBody,
      };
    }

    return response.json();
  } catch (error: any) {
    console.error("Error confirming mapping:", error);
    throw {
      status: error?.status || 500,
      data: { message: error?.data?.errors[0] ?? "Unknown error occurred" },
    };
  }

};


export interface PciPortalProperty {
  mid: string;
  name: string;
  property_id: string | null;
  mapping_status?: string,
  count?: number;
}

export const getExternalMerchants = async (
  tenantId: number,
  providerId: number,
  merchantName?: string,
  mid?: string,
  visibleCount?: number,
  status?: string,
): Promise<PciPortalProperty[]> => {
  const url = !!visibleCount ? `${baseUrl}/external-merchants?tenant_id=${tenantId}&provider_id=${providerId}&merchant_name=${merchantName}&status=unmapped&page=${visibleCount}&per_page=10` :
    status === "mapped" ? `${baseUrl}/external-merchants?tenant_id=${tenantId}&provider_id=${providerId}&merchant_name=${merchantName}&status=mapped` : mid == null
      ? `${baseUrl}/external-merchants?tenant_id=${tenantId}&provider_id=${providerId}&merchant_name=${merchantName}&status=unmapped`
      : `${baseUrl}/external-merchants?tenant_id=${tenantId}&provider_id=${providerId}&merchant_name=${merchantName}&mids=${mid}`;

  try {
    const response = await authorizedFetch(url, { method: 'GET', headers: getAuthHeaders(), });
    if (!response.ok) {
      throw new Error(`API call failed with status: ${response.status}`);
    }
    const responseData = await response.json();
    if (!!visibleCount) {
      return responseData.data.map(
        (item: {
          merchant_name: string;
          external_mid: string;
          mapping_status?: string | null;
          property_id: string | null;
        }) => ({
          name: item.merchant_name,
          mid: item.external_mid,
          mapping_status: item?.mapping_status ?? "mapped",
          property_id: item.property_id,
          count: responseData.count, 
        })
      );
    }

    return responseData.data.map((item: { merchant_name: string; external_mid: string; mapping_status?: string | null; property_id: string | null; }) => ({
      name: item.merchant_name,
      mid: item.external_mid,
      mapping_status: item?.mapping_status ?? "mapped",
      property_id: item.property_id,
    }));
  } catch (error) {
    console.error("Error fetching external merchants:", error);
    throw error;
  }
};

export const getExistingMappedMerchants = async (
  tenantId: number,
  providerId: number,
  mappingStatus?: string,
): Promise<MappingChange[]> => {
  let mappingStatusParam = 'active';
  if (mappingStatus === "unmapped") {
    mappingStatusParam = 'unmapped';
  }
  const url = `${baseUrl}/property-merchant-mappings?tenant_id=${tenantId}&provider_id=${providerId}&status=${mappingStatusParam}`;

  try {
    const response = await authorizedFetch(url, { method: 'GET', headers: getAuthHeaders() });
    if (!response.ok) {
      throw new Error(`API call failed with status: ${response.status}`);
    }
    const responseData = await response.json();

    const mappedData = responseData.data.property_merchant_mapping.map((item: any) => ({
      id: String(item.property_id),
      venzaProperty: item.property_name,
      pciPortalProperty: item.external_merchant_name,
      venzaPropertyId: String(item.property_id),
      pciPortalPropertyId: item.external_mid,
      mid: item.external_mid,
      status: item.status,
      changeType: "unchanged",
      action: null
    }));

    return [...mappedData, {
      id: "add-row",
      venzaProperty: "",
      venzaPropertyId: null,
      pciPortalPropertyId: null,
      pciPortalProperty: "",
      status: "active", // Default status for new row
      changeType: "unchanged",
      action: "",
      isAddRow: true
    }];

  } catch (error) {
    console.error("Error fetching external merchants:", error);
    throw error;
  }
};